import { addToCart } from 'analytics/snowplow/events'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import TextButton from 'LuxKit/Button/TextButton'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightViewTypes } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { sum } from 'lib/array/arrayUtils'
import { generateMultipleRoomCheckoutItems } from 'lib/checkout/accommodation/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import React, { useCallback, useContext, useMemo } from 'react'
import * as Analytics from 'analytics/analytics'
import { PackageOptionViewByRoom, LoadingStatusByRoom, FlightsInfoByRoom, PackagesOptionsViewsByRoom, getCalendarsToBeFetchedByRoom, getDurationPackageOptionsByRoom, getFlightsInfoByRoom, isLoadingByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import moment from 'moment'
import Group from 'components/utils/Group'
import { isAgentHubMultiCartItemsEnabled } from 'agentHub/selectors/agentHubSelectors'
import { pluralizeToString } from 'lib/string/pluralize'
import { allocateTravellersToRooms } from 'businessTraveller/utils/allocateTravellersToRooms'

interface Props extends React.ComponentProps<typeof TextButton> {
  rooms: Array<App.RoomOccupants>;
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary;
  checkIn?: string;
  duration?: number;
  activePackagesByRoom: PackageOptionViewByRoom;
  isLoadingByRoom: LoadingStatusByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
  flightsInfoByRoom: FlightsInfoByRoom;
  durationPackagesByRoom: PackagesOptionsViewsByRoom;
  loggedIn?: boolean;
  currencyCode: string;
  isMultiRoomConnected: boolean;
  airport?: App.Airport;
  disabled?: boolean;
  onProceedToBooking: (items: Array<App.Checkout.AnyItem>, connectRooms: boolean, keepBrowsing: boolean) => void;
  selectedTravellerEmployeeIds: Array<string> | undefined;
  selectedTravellerGuestCount: number | undefined;
  selectedTravellerGuestIds?: Array<string> | undefined;
}

function MultiRoomOfferCheckoutButton(props: Props) {
  const {
    rooms,
    offer,
    checkIn,
    duration,
    activePackagesByRoom,
    isLoadingByRoom,
    calendarsToBeFetchedByRoom,
    flightsInfoByRoom,
    durationPackagesByRoom,
    loggedIn,
    currencyCode,
    isMultiRoomConnected,
    airport,
    disabled,
    children,
    onProceedToBooking,
    selectedTravellerEmployeeIds,
    selectedTravellerGuestCount,
    selectedTravellerGuestIds,
    ...rest
  } = props

  const showModal = useContext(ModalContext)

  const offerPageDispatch = useOfferPageDispatch()
  const packagesByRoom = useMemo(() => {
    const selectedPackages = { ...activePackagesByRoom }
    Object
      .entries(selectedPackages)
      // filters only rooms without package
      .filter(([, selectedPackage]) => !selectedPackage)
      .forEach(([roomId]) => {
        const isThisRoomLoading = isLoadingByRoom[roomId] || calendarsToBeFetchedByRoom[roomId]
        // If this room has a "loading" state, we select
        // the first available package for it, only for the
        // component to show the loading indicator
        selectedPackages[roomId] = isThisRoomLoading ? durationPackagesByRoom[roomId][0] : undefined
      })

    return selectedPackages
  }, [activePackagesByRoom, isLoadingByRoom, durationPackagesByRoom, calendarsToBeFetchedByRoom])

  const proceedToBook = useCallback((keepBrowsing: boolean) => {
    if (!checkIn) return
    const checkOut = moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT)
    const dates = { checkIn, checkOut }

    // Allocate travellers to rooms
    const roomsWithTravellerInfo = allocateTravellersToRooms(
      rooms,
      selectedTravellerEmployeeIds,
      selectedTravellerGuestIds,
    )

    const accommodationItems = generateMultipleRoomCheckoutItems(packagesByRoom, offer, dates, roomsWithTravellerInfo, currencyCode)
    // For now our only option is to connect all rooms as a single group or none
    const flightItems: Array<App.Checkout.FlightItem> = []
    const roomsWithFlights = roomsWithTravellerInfo?.filter(room => flightsInfoByRoom[room.roomId]?.isFlightsSelected) ?? []
    if (roomsWithFlights.length > 0 && airport) {
      const flightOccupants = countOccupantsForFlights(roomsWithFlights)
      flightItems.push(generateFlightCheckoutItem({
        originAirportCode: airport.code,
        destinationAirportCode: offer.flightDestinationPort,
        bundledItemIds: accommodationItems.map(item => item.itemId),
        occupants: flightOccupants,
        fareType: 'return',
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        passengers: generatePassengersFromOccupants(flightOccupants),
        quotedFare: sum(roomsWithFlights, room => flightsInfoByRoom[room.roomId]?.flightPrice ?? 0),
      }))
    }
    Analytics.trackEvent(addToCart())
    onProceedToBooking([...accommodationItems, ...flightItems], isMultiRoomConnected, keepBrowsing)
  }, [checkIn, duration, rooms, selectedTravellerEmployeeIds, selectedTravellerGuestIds, packagesByRoom, offer, currencyCode, airport, onProceedToBooking, isMultiRoomConnected, flightsInfoByRoom])

  const onBookNowClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>, keepBrowsing: boolean = false) => {
    if (!checkIn) {
      e.preventDefault()

      offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else if (!loggedIn) {
      e.preventDefault()

      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)

      if (result.loggedIn && !isRoomsInvalid(rooms)) {
        proceedToBook(keepBrowsing)
      }
    } else if (!isRoomsInvalid(rooms)) {
      proceedToBook(keepBrowsing)
    }
  }, [checkIn, loggedIn, rooms, offerPageDispatch, showModal, proceedToBook])

  const onAddToCartClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    onBookNowClick(e, true)
  }, [onBookNowClick])

  return <Group direction="horizontal" gap={8}>
    <TextButton
      {...rest}
      type="submit"
      onClick={onBookNowClick}
      disabled={disabled}
    >
      {children}
    </TextButton>
    {!!checkIn && isAgentHubMultiCartItemsEnabled && <TextButton
      kind="secondary"
      type="submit"
      onClick={onAddToCartClick}
      disabled={disabled}
    >
      {`Add ${pluralizeToString('room', rooms.length)} to cart`}
    </TextButton>}
  </Group>
}

export default withOfferPageAndReduxState((state, appState) => ({
  currencyCode: appState.geo.currentCurrency,
  checkIn: state.checkInDate,
  duration: state.duration,
  rooms: state.rooms,
  offer: state.offer,
  isMultiRoomConnected: state.isMultiRoomConnected,
  isLoadingByRoom: isLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
  durationPackagesByRoom: getDurationPackageOptionsByRoom(state, appState),
  loggedIn: selectLoggedIn(appState),
  airport: state.airport,
  selectedTravellerEmployeeIds: appState.businessTraveller.selectedTravellerEmployeeIds,
  selectedTravellerGuestCount: appState.businessTraveller.selectedTravellerGuestCount,
  selectedTravellerGuestIds: appState.businessTraveller.selectedTravellerGuestIds,
}), MultiRoomOfferCheckoutButton)
